import React from "react"
import PropTypes from "prop-types"
import { JWT_HOST_API } from "configs/auth.config"

const sampleReplace = (text) => {
  if (!text) return ""
  return text
    .replace(/\{\{\s*1\s*\}\}/g, "John Doe")
    .replace(/\{\{\s*2\s*\}\}/g, "ORD-12345")
    .replace(/\{\{\s*3\s*\}\}/g, "Tomorrow 3PM")
}

const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (typeof url !== 'string') return url;
  
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  
  // Prepend base URL for relative paths
  const base = JWT_HOST_API || '';
  const apiHost = base.endsWith('/') ? base : (base ? `${base}/` : '');
  const cleanPath = url.startsWith('/') ? url.substring(1) : url;
  
  return `${apiHost}${cleanPath}`;
};

function MediaHeader({ format, mediaUrl, isLoading: propLoading }) {
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setError(false);
    setLoading(true);
  }, [mediaUrl]);

  if (!format || format === "TEXT") return null

  // If we are explicitly told we are loading (e.g. fetching blob), show spinner
  if (propLoading && format === "IMAGE") {
    return (
      <div className="w-full h-48 bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const resolvedUrl = resolveMediaUrl(mediaUrl);

  const renderFallback = (icon, title, message) => (
    <div className="w-full h-48 bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center">
      <div className="text-center p-4">
        {icon || (
          <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.587-1.587a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <p className="text-xs text-gray-400 mt-1">{message}</p>
      </div>
    </div>
  );

  if (format === "IMAGE") {
    if (!resolvedUrl) {
      return renderFallback(null, "Add Image URL", "Enter an image URL to preview");
    }

    return (
      <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        {error ? (
          renderFallback(
            <svg className="w-10 h-10 mx-auto text-red-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>,
            "Failed to load image",
            "Check if the URL is valid and accessible"
          )
        ) : (
          <img 
            src={resolvedUrl} 
            alt="Header content" 
            className={`w-full h-full object-cover transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => setLoading(false)}
            onError={() => {
              console.error("Image load failed:", resolvedUrl);
              setError(true);
              setLoading(false);
            }}
          />
        )}
      </div>
    )
  }

  return null
}

const WhatsAppPreview = ({
  headerText,
  headerFormat,
  bodyText,
  footerText,
  buttons = [],
  mediaUrl = null,
  isLoading = false,
}) => {
  const header = sampleReplace(headerText)
  const body = sampleReplace(bodyText)
  const footer = sampleReplace(footerText)

  return (
    <div
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-full max-w-sm mx-auto"
      style={{
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      role="region"
      aria-label="WhatsApp message preview"
    >
      {/* Top bar (Business header) */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-green-500 rounded-full flex items-center justify-center shadow-sm border border-green-600">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">Your Business</div>
              <div className="text-xs text-gray-500 font-medium">✓ Verified Business</div>
            </div>
          </div>
          <div className="text-xs text-gray-400 font-medium" aria-label="timestamp">
            07:44
          </div>
        </div>
      </div>

      {/* Message container */}
      <div className="p-2 pb-3 bg-gray-100">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header media/text */}
          <MediaHeader format={headerFormat} mediaUrl={mediaUrl} isLoading={isLoading} />

          {/* Header text for TEXT format or when provided with media */}
          {header && (
            <div
              className={`px-3 ${headerFormat && headerFormat !== "TEXT" ? "py-2 border-b border-gray-100" : "pt-3"}`}
            >
              <div className="font-bold text-gray-900 text-sm leading-tight">{header}</div>
            </div>
          )}

          {/* Body + Footer */}
          <div
            className={`px-3 ${!header && (!headerFormat || headerFormat === "TEXT" || !mediaUrl) ? "pt-3" : ""} pb-3`}
          >
            {body && (
              <div className="mb-3">
                <p className="text-xs text-gray-800 whitespace-pre-line leading-relaxed">{body}</p>
              </div>
            )}

            {footer && (
              <div className="mb-1">
                <p className="text-xs text-gray-500 italic font-medium">{footer}</p>
              </div>
            )}

            {/* Meta: time + read ticks */}
            <div className="flex items-center justify-end mt-2">
              <span className="text-xs text-gray-400 mr-2 font-medium">07:44</span>
              <div className="flex space-x-0.5" aria-label="read status">
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        {buttons && buttons.length > 0 && (
          <div className="mt-2 space-y-1.5" aria-label="template buttons">
            {buttons.map((button, index) => {
              const icon =
                button.type === "URL" ? (
                  <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : button.type === "QUICK_REPLY" ? (
                  <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                )

              return (
                <button
                  key={`${button.text}-${index}`}
                  type="button"
                  className="w-full py-2 px-3 bg-white border border-gray-300 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 transition-colors duration-200 flex items-center justify-center shadow-sm active:scale-[0.98]"
                  aria-label={button.text}
                >
                  {icon}
                  <span className="truncate">{button.text}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

WhatsAppPreview.propTypes = {
  headerText: PropTypes.string,
  headerFormat: PropTypes.oneOf(["TEXT", "IMAGE"]),
  bodyText: PropTypes.string,
  footerText: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      type: PropTypes.oneOf(["URL", "QUICK_REPLY", "PHONE_NUMBER"]),
      url: PropTypes.string,
    }),
  ),
  mediaUrl: PropTypes.string,
  isLoading: PropTypes.bool,
}

export default WhatsAppPreview
